import { useState, useEffect, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { settingsScrollIdForPath, settingsProgramScrollAnchor } from "@/components/settings/SettingsLayout";
// MS530: `format` (date-fns) was previously imported solely for
// ReferAFriendBlock's credit-expiry line. Refer-a-friend moved to
// its own /refer page, leaving no Settings caller for date-fns;
// the import was removed in the same commit to avoid an unused
// symbol. MS557 brings it back — the audit history list formats the
// created_at timestamp into human-friendly "12 May 2026" rows.
import { format, parseISO } from "date-fns";
import { Trash2, Plus, Pencil, CreditCard, Loader2, CheckCircle2, Sparkles, ArrowRight, ChevronDown } from "lucide-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useProfile,
  useCompanySettings,
  useCompanyAddresses,
  useCurrentBudgetPeriod,
  useAdjustmentsForPeriod,
  useManualApprovalProgress,
  MANUAL_APPROVALS_FOR_AUTOPILOT,
  type CompanyAddressRow,
} from "@/hooks/queries";
// MS557: shared adjustment modal between Dashboard and Settings.
import { BudgetAdjustmentModal } from "@/components/dashboard/BudgetAdjustmentModal";
import { trimmedStringSchema, emailSchema } from "@/lib/schemas";
// Sprint 2.5a /settings-fix, single source of truth for the option
// lists. Settings was hardcoding its own subsets that drifted from
// what /onboarding writes to the same DB column. Importing from
// companyPrefs.ts (the canonical home) means /onboarding +
// /settings now agree on the value space.
import {
  BUDGET_OPTIONS,
  INDUSTRY_OPTIONS,
  TEAM_SIZE_OPTIONS,
  normalizeMonthlyBudget,
} from "@/lib/companyPrefs";
// MS388: live Notification cadence controls. parseNotificationPrefs
// is the frontend mirror of supabase/functions/_shared/notification-prefs.ts;
// the two share the canonical default + same shape so what the buyer
// toggles in /settings is exactly what the scheduled jobs read.
import {
  CELEBRATION_LEAD_DAY_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  DEFAULT_NOTIFICATION_PREFS,
  parseNotificationPrefs,
  type DayOfWeek,
  type NotificationPrefs,
} from "@/lib/notificationPrefs";
// MS388 quick-win: title-case office addresses on SAVE so lowercase
// input (incl. Places autocomplete picks like "11 wilson street, south
// yarra 3141") doesn't leak into vendor briefs and order pickers.
import { normalizeAddressString } from "@/lib/normalizeAddress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
// MS289: Google Places autocomplete drop-in for office-address inputs
// (edit row + add row). Silently degrades to plain text if Maps load
// fails, so no fallback branch needed.
import { AddressAutocomplete } from "@/components/lembra/AddressAutocomplete";
// MS555: Settings → Tiers panel. CRUD + employee picker with inline
// dedupe per MS552 §3. Encapsulated in its own component so the page
// stays under 2,100 LOC and the test surface stays focused. The
// component owns its own data hooks, mutations, and modal state.
import { TiersPanel } from "@/components/lembra/TiersPanel";
import { BulkCelebrationSettingsPanel } from "@/components/bulk/BulkCelebrationSettingsPanel";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
// MS554: Budget panel uses a click-to-expand inline explainer per
// section 2 of the wireframes brief — modals are reserved for
// destructive flows (delete account, remove card).
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "@/integrations/stripe/client";

interface SavedCard {
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
}

// Inline-form schemas for the Company Information and Office Addresses
// sections. Kept in-file because they're local to Settings; the shared
// src/lib/schemas module is for cross-form primitives, not per-page
// compositions.
// MS94: teamSize added to the company-info form schema so customers
// can keep their team-size bucket up to date as the team grows
// post-onboarding. Optional + default "" so the form doesn't block
// on legacy rows that pre-date this field.
const companyInfoSchema = z.object({
  name: trimmedStringSchema({ min: 1, max: 200, label: "your company name" }),
  industry: z.string(),
  teamSize: z.string(),
});
type CompanyInfoForm = z.infer<typeof companyInfoSchema>;

const addressSchema = z.object({
  label: trimmedStringSchema({ min: 1, max: 60, label: "a label" }),
  address: trimmedStringSchema({ min: 1, max: 200, label: "an address" }),
});
type AddressForm = z.infer<typeof addressSchema>;

// ── Card brand logo helper ────────────────────────────────────────
function brandLabel(brand: string) {
  const map: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "Amex",
    discover: "Discover",
    jcb: "JCB",
    unionpay: "UnionPay",
    unknown: "Card",
  };
  return map[brand.toLowerCase()] ?? brand;
}

// ── Inner form, needs Stripe context ────────────────────────────
function CardSetupForm({
  companyId,
  userEmail,
  companyName,
  onSaved,
}: {
  companyId: string;
  userEmail: string;
  companyName: string;
  onSaved: (card: SavedCard) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    try {
      // 1. Create SetupIntent on the server
      const { data, error: fnError } = await supabase.functions.invoke("setup-payment-method", {
        body: { mode: "setup", companyId, customerEmail: userEmail, companyName },
      });
      if (fnError || data?.error) throw new Error(fnError?.message ?? data?.error ?? "Setup failed");

      const { clientSecret } = data;

      // 2. Confirm the card setup with Stripe Elements
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error("Card element not found");

      const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { email: userEmail },
        },
      });

      if (confirmError) throw new Error(confirmError.message);
      if (!setupIntent?.payment_method) throw new Error("Setup incomplete");

      // 3. Save PM metadata to Supabase via Edge Function
      const pmId =
        typeof setupIntent.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;

      const { data: saved, error: saveError } = await supabase.functions.invoke(
        "setup-payment-method",
        { body: { mode: "save", companyId, paymentMethodId: pmId } },
      );
      if (saveError || saved?.error) throw new Error(saveError?.message ?? saved?.error ?? "Save failed");

      onSaved(saved as SavedCard);
      toast.success("Card saved. Future orders will be charged automatically.");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg border border-charcoal/10 bg-white px-4 py-3">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "15px",
                color: "#1D3461",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                "::placeholder": { color: "#9ca3af" },
              },
              invalid: { color: "#E07B6C" },
            },
          }}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {saving ? "Saving…" : "Save card"}
        </Button>
      </div>
    </form>
  );
}

/**
 * SectionCard — MS37 Heidi-system upgrade.
 *
 * Each section now carries the same editorial pattern as the rest of
 * the authed app:
 *   - mono-uppercase eyebrow (small caps coral) for category
 *   - Newsreader serif title with optional italic-emphasis on the
 *     operative word (e.g. "Refer a friend" emphasises "friend")
 *   - Optional DM Sans description below
 *
 * Backwards-compatible: pass just `title` and you get a Newsreader
 * serif h3 in navy. Pass `eyebrow` to surface the category chip.
 * Pass `emphasis` to italicise a substring of the title in coral.
 */
/**
 * Small inline "Show an example" disclosure. Mirrors the budget panel's
 * "How does this work?" Collapsible (coral toggle + bordered content box)
 * so the per-control examples read consistently across the Program tab.
 * Self-contained open/close state so callers don't thread it through the
 * large Settings component.
 */
function ExampleDisclosure({
  label = "Show an example",
  children,
}: {
  label?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-coral hover:text-coral/80"
        >
          {open ? "Hide example" : label}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 rounded-lg border border-charcoal/10 bg-white p-3 text-xs leading-relaxed text-charcoal/75">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function SectionCard({
  title,
  emphasis,
  eyebrow,
  description,
  children,
  danger,
  id,
}: {
  title: string;
  /** Substring of `title` to italicise in coral. Case-insensitive match. */
  emphasis?: string;
  /** Mono-uppercase category chip above the title. */
  eyebrow?: string;
  /** DM Sans paragraph below the title. */
  description?: string;
  children?: React.ReactNode;
  danger?: boolean;
  /** Used for deep-linking, e.g. /settings#payment scrolls here. */
  id?: string;
}) {
  const titleColor = danger ? "#C4564A" : "#1D3461";
  const renderTitle = () => {
    if (!emphasis) return title;
    const idx = title.toLowerCase().indexOf(emphasis.toLowerCase());
    if (idx === -1) return title;
    return (
      <>
        {title.slice(0, idx)}
        <em style={{ fontStyle: "italic", fontWeight: 500, color: "#C4564A" }}>
          {title.slice(idx, idx + emphasis.length)}
        </em>
        {title.slice(idx + emphasis.length)}
      </>
    );
  };

  return (
    <div
      id={id}
      className={`rounded-xl border p-6 scroll-mt-24 ${
        danger ? "border-coral/40" : "border-charcoal/10"
      }`}
    >
      <header className="mb-4">
        {eyebrow ? (
          <div
            style={{
              fontFamily:
                "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(196,86,74,0.85)",
              marginBottom: 6,
            }}
          >
            {eyebrow}
          </div>
        ) : null}
        <h3
          style={{
            fontFamily: "Newsreader, Georgia, serif",
            fontWeight: 500,
            fontSize: "1.35rem",
            lineHeight: 1.18,
            letterSpacing: "-0.012em",
            color: titleColor,
            margin: 0,
          }}
        >
          {renderTitle()}
        </h3>
        {description ? (
          <p
            className="mt-1.5 text-sm"
            style={{
              fontFamily: "DM Sans, sans-serif",
              color: "rgba(45,41,38,0.7)",
              lineHeight: 1.55,
            }}
          >
            {description}
          </p>
        ) : null}
      </header>
      {children}
    </div>
  );
}

/**
 * MS388: shared row layout for the Notification cadence email toggles.
 * Lifted to keep the JSX in the section card readable when there are
 * three near-identical rows. Mirrors the Concierge Autopilot toggle
 * row (title + helper + Switch on the right) so the visual rhythm is
 * consistent through the page.
 */
// ── MS557 helpers ────────────────────────────────────────────────────
//
// formatPeriodLabel mirrors DashboardPoolWidget.periodLabel for the
// adjustment modal's subtitle copy. Kept private to Settings rather
// than re-exporting from the widget — the dashboard widget exports
// the helper for tests, but the modal's subtitle is a presentation
// concern of *this* page and bleeding it back through the widget
// barrel would couple two surfaces unnecessarily.
function formatPeriodLabel(
  cadence: "weekly" | "monthly" | "quarterly" | "per_celebration",
  periodStart: string,
): string {
  if (cadence === "per_celebration") return "Current period";
  const start = parseISO(periodStart);
  if (cadence === "monthly") return format(start, "MMMM yyyy");
  if (cadence === "quarterly") {
    const month = start.getMonth();
    const quarter = Math.floor(month / 3) + 1;
    return `Q${quarter} ${format(start, "yyyy")}`;
  }
  return `Week of ${format(start, "d MMM")}`;
}

/**
 * MS557: audit-history surface for the Budget panel. Read-only list
 * of every company_budget_adjustments row for the current period,
 * most-recent first. Collapsed by default — the panel stays light
 * for buyers who don't audit. Caps at 10 by default; "Show all" expands
 * to the full set (which the parent hook caps at 50 — well above the
 * "last 10" UI cap so the toggle is client-side only).
 *
 * INSERT-only by design — no edit/delete affordances anywhere. Per
 * MS552 §5: "Each entry shows date, signed amount, actor, reason. No
 * edit or void button — INSERT-only audit."
 */
interface BudgetAdjustmentRowForUI {
  id: string;
  amount_cents: number;
  kind: "one_time" | "permanent";
  reason: string | null;
  actor_name: string | null;
  actor_email: string | null;
  created_at: string;
}

function BudgetAdjustmentsHistory({
  adjustments,
  isLoading,
  isOpen,
  onToggle,
  expanded,
  onToggleExpanded,
}: {
  adjustments: BudgetAdjustmentRowForUI[];
  isLoading: boolean;
  isOpen: boolean;
  onToggle: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  // 10-row default cap per dispatch spec; the parent passes up to 50.
  const DEFAULT_LIMIT = 10;
  const hasMore = adjustments.length > DEFAULT_LIMIT;
  const rows = expanded ? adjustments : adjustments.slice(0, DEFAULT_LIMIT);

  // Cents → "$X.XX" with sign prefix. Negative amounts render with a
  // leading "−" using the unicode minus glyph for visual weight; sign
  // is the load-bearing affordance per wireframes §5 ("$500 top-up
  // vs $500 reduction must read at a glance").
  const formatSigned = (cents: number) => {
    const abs = Math.abs(cents);
    const dollars = (abs / 100).toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return cents < 0 ? `−$${dollars}` : `+$${dollars}`;
  };

  return (
    <div
      className="mt-3 rounded-lg border border-charcoal/10 bg-white"
      data-testid="budget-adjustments-history"
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
        aria-expanded={isOpen}
        data-testid="budget-adjustments-history-toggle"
      >
        <span className="text-sm font-semibold text-navy">
          Activity log
          {adjustments.length > 0 ? (
            <span className="ml-2 text-xs font-normal text-charcoal/55">
              ({adjustments.length}{" "}
              {adjustments.length === 1 ? "change" : "changes"})
            </span>
          ) : null}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-charcoal/60 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen ? (
        <div
          className="border-t border-charcoal/10 px-3 py-2"
          data-testid="budget-adjustments-history-list"
        >
          {isLoading ? (
            <p className="py-3 text-xs text-charcoal/55">Loading…</p>
          ) : adjustments.length === 0 ? (
            <p className="py-3 text-xs text-charcoal/55">
              No adjustments yet for this period.
            </p>
          ) : (
            <>
              <ul className="divide-y divide-charcoal/5">
                {rows.map((adj) => {
                  const created = parseISO(adj.created_at);
                  const dateLabel = format(created, "d MMM yyyy");
                  const actor =
                    adj.actor_name ??
                    adj.actor_email ??
                    "Removed user";
                  const isReduction = adj.amount_cents < 0;
                  return (
                    <li
                      key={adj.id}
                      className="py-2.5"
                      data-testid="budget-adjustments-history-row"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-sm">
                            <span
                              className={`font-semibold tabular-nums ${
                                isReduction
                                  ? "text-[#C4564A]"
                                  : "text-navy"
                              }`}
                            >
                              {formatSigned(adj.amount_cents)}
                            </span>
                            <span
                              className="rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                              style={{
                                background:
                                  adj.kind === "permanent"
                                    ? "rgba(29,52,97,0.08)"
                                    : "rgba(196,86,74,0.10)",
                                color:
                                  adj.kind === "permanent"
                                    ? "rgba(29,52,97,0.75)"
                                    : "#C4564A",
                              }}
                            >
                              {adj.kind === "permanent"
                                ? "Permanent"
                                : "One-time"}
                            </span>
                          </div>
                          {adj.reason ? (
                            <p className="mt-1 text-xs text-charcoal/70 break-words">
                              {adj.reason}
                            </p>
                          ) : null}
                        </div>
                        <div className="text-right text-xs text-charcoal/55 whitespace-nowrap">
                          <div>{dateLabel}</div>
                          <div className="truncate max-w-[120px]">{actor}</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {hasMore ? (
                <button
                  type="button"
                  onClick={onToggleExpanded}
                  className="mt-2 text-xs text-charcoal/65 underline underline-offset-2"
                  data-testid="budget-adjustments-history-show-more"
                >
                  {expanded
                    ? "Show fewer"
                    : `Show all ${adjustments.length}`}
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function NotificationToggleRow({
  id,
  title,
  description,
  checked,
  onCheckedChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <Label
          htmlFor={id}
          className="text-sm font-semibold text-navy cursor-pointer"
        >
          {title}
        </Label>
        <p className="text-xs text-charcoal/65 mt-0.5 max-w-md">
          {description}
        </p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={title}
      />
    </div>
  );
}

export default function SettingsPage() {
  const { user, resetPassword } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const activeSection = settingsScrollIdForPath(location.pathname);
  const programScrollAnchor =
    settingsProgramScrollAnchor(location.pathname) ??
    (location.hash.replace(/^#/, "") || null);

  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;
  const profileFullName = profileQuery.data?.full_name ?? null;
  const profileEmail = profileQuery.data?.email ?? null;

  const companyQuery = useCompanySettings(companyId);
  const addressesQuery = useCompanyAddresses(companyId);

  useEffect(() => {
    if (activeSection !== "program" || !programScrollAnchor) return;

    const scrollToAnchor = () => {
      const el = document.getElementById(programScrollAnchor);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    scrollToAnchor();
    const retry = window.setTimeout(scrollToAnchor, 150);
    return () => window.clearTimeout(retry);
  }, [activeSection, programScrollAnchor, companyQuery.isSuccess]);

  const manualApprovalQuery = useManualApprovalProgress(companyId);
  const manualApprovalUnlocked = manualApprovalQuery.data?.unlocked ?? false;
  const manualApprovalCount = manualApprovalQuery.data?.count ?? 0;
  const manualApprovalRemaining = manualApprovalQuery.data?.remaining ?? MANUAL_APPROVALS_FOR_AUTOPILOT;

  // Sprint 1.3, autopilot eligibility breakdown for the team. Drives
  // the "X of Y team members are autopilot-eligible" copy in the new
  // Concierge Autopilot section. Cheap COUNT-only query.
  const eligibilityQuery = useQuery({
    queryKey: ["companies", companyId, "autopilot_eligibility_summary"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<{ total: number; eligible: number }> => {
      if (!companyId) return { total: 0, eligible: 0 };
      // Two head-only queries (no row data fetched); cheap.
      const [totalRes, eligibleRes] = await Promise.all([
        supabase
          .from("team_members")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .eq("is_active", true),
        supabase
          .from("team_members")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .eq("is_active", true)
          .eq("auto_eligible", true),
      ]);
      return {
        total: totalRes.count ?? 0,
        eligible: eligibleRes.count ?? 0,
      };
    },
  });

  // Sprint 1.3, upcoming auto-fires preview. Shows the next 5
  // celebrations within 30 days where the recipient is auto_eligible,
  // so the user can see exactly which gifts will fire if autopilot
  // stays on. Inner-join on team_members enforces the eligibility gate
  // at query time (matches what the cron does).
  const upcomingFiresQuery = useQuery({
    queryKey: ["companies", companyId, "autopilot_upcoming_fires"],
    enabled: Boolean(companyId),
    queryFn: async () => {
      if (!companyId) return [];
      const today = new Date();
      const horizon = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("celebrations")
        .select("id, type, date, team_members!inner(id, name, auto_eligible)")
        .eq("company_id", companyId)
        .eq("status", "upcoming")
        .eq("team_members.auto_eligible", true)
        .gte("date", fmt(today))
        .lte("date", fmt(horizon))
        .order("date", { ascending: true })
        .limit(5);
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        type: string;
        date: string;
        team_members: { id: string; name: string; auto_eligible: boolean } | { id: string; name: string; auto_eligible: boolean }[];
      }>;
    },
  });

  // Editable account fields (drafts, initialised from profile once loaded)
  const [fullNameDraft, setFullNameDraft] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  // MS256: re-auth gate for email changes. Without this, a session-
  // hijacked attacker (shared device, stolen unlocked laptop) could
  // change the account email → request password reset to the new
  // email → lock the legitimate owner out. Mirror Supabase's recommended
  // pattern: probe the current password via signInWithPassword before
  // calling updateUser({email}).
  const [emailReAuthOpen, setEmailReAuthOpen] = useState(false);
  const [emailReAuthPassword, setEmailReAuthPassword] = useState("");
  const [emailReAuthError, setEmailReAuthError] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  // Company info form (rhf + zod). Synced from the company query below.
  // MS94: keep companyForm aware of teamSize so the existing onSubmit
  // can persist it alongside name + industry without a separate flow.
  const companyForm = useForm<CompanyInfoForm>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: { name: "", industry: "", teamSize: "" },
  });
  const companyName = companyForm.watch("name");

  // Addresses (list comes from the query; both forms below are separate).
  const addresses: CompanyAddressRow[] = addressesQuery.data ?? [];
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [showAddAddress, setShowAddAddress] = useState(false);
  // MS400: track in-flight deletes so the delete button can't be double-tapped
  const [deletingAddressId, setDeletingAddressId] = useState<string | null>(null);

  // Add-new-address form.
  const addAddressForm = useForm<AddressForm>({
    resolver: zodResolver(addressSchema),
    defaultValues: { label: "", address: "" },
  });

  // Edit-existing-address form (one instance; re-reset when the edit target
  // changes). Simpler than keying a form per row.
  const editAddressForm = useForm<AddressForm>({
    resolver: zodResolver(addressSchema),
    defaultValues: { label: "", address: "" },
  });

  // Celebration prefs.
  const [monthlyBudget, setMonthlyBudget] = useState("");
  // MS90: company-level default tier control. Wired into MS79's
  // recommendation engine path — saving here flows through to the
  // Dashboard's per-celebration recommendations (and per-recipient
  // tier_override on team_members still wins when set).
  const [defaultTier, setDefaultTier] = useState<"" | "standard" | "premium" | "luxury">("");

  // ── MS554 Budget panel state ──────────────────────────────────────
  //
  // Cadence is the master switch — see wireframes §2 + locked
  // decisions in the dispatch brief. The picker always defaults to
  // `per_celebration` for first-time companies (pool_cadence === null)
  // so the panel renders the lowest-friction default rather than an
  // empty form.
  //
  // Cadence write semantics:
  //   - per_celebration ↔ pooled: write to pool_cadence directly. The
  //     transition crosses the pool/no-pool boundary and there's no
  //     current pooled period to preserve.
  //   - pooled → pooled (e.g. monthly → quarterly): write to
  //     next_cadence and surface an inline confirmation. Per MS551 the
  //     current period continues on the old cadence; the rollover cron
  //     applies the staged change at the next boundary.
  //   - Hydration: the picker reflects (next_cadence ?? pool_cadence
  //     ?? 'per_celebration') so a user who already staged a change
  //     sees their staged value selected, not the still-active one.
  type PoolCadence = "per_celebration" | "weekly" | "monthly" | "quarterly";
  const [cadence, setCadence] = useState<PoolCadence>("per_celebration");
  const [poolSizeInput, setPoolSizeInput] = useState<string>(""); // dollars, blank when no pool
  const [perCelebrationCeilingInput, setPerCelebrationCeilingInput] = useState<string>(""); // dollars
  const [rolloverOn, setRolloverOn] = useState<boolean>(true); // default ON per wireframes
  const [savingBudget, setSavingBudget] = useState(false);
  const [budgetExplainerOpen, setBudgetExplainerOpen] = useState(false);
  // MS559: first-run state for the Budget panel. `hasConfiguredBudget`
  // tracks whether the company has ever saved a cadence — null
  // pool_cadence in the DB means a brand-new company that's never
  // visited this panel. When false we show a get-started empty state
  // instead of the bare form (per wireframes §8); `budgetSetupRevealed`
  // flips to show the full form when the buyer clicks "Set up my
  // budget". A buyer can still walk away without saving (the bracket
  // fallback stays active) — nothing here blocks core platform use.
  const [hasConfiguredBudget, setHasConfiguredBudget] = useState(true);
  const [budgetSetupRevealed, setBudgetSetupRevealed] = useState(false);
  // Track the cadence as last loaded from the DB (effective + staged)
  // so the save handler can detect (a) whether the new selection is a
  // pooled→pooled transition (writes next_cadence) vs a per_cel↔pooled
  // transition (writes pool_cadence), and (b) whether to surface the
  // "staged for next period" inline confirmation. Hydrated alongside
  // the inputs in the useEffect below.
  const [savedEffectiveCadence, setSavedEffectiveCadence] =
    useState<PoolCadence>("per_celebration");
  const [stagedNextCadence, setStagedNextCadence] =
    useState<PoolCadence | null>(null);

  // ── MS557 Adjustment modal + audit history state ──────────────────
  //
  // The Settings → Budget panel is the second entry point into the
  // shared BudgetAdjustmentModal (the Dashboard pool widget is the
  // other). Per MS552 §5 both paths render the same form/modal, so we
  // import the same component rather than duplicating the layout.
  //
  // `adjustOpen` toggles the modal. `historyOpen` toggles the audit
  // section (collapsed by default per wireframes §5 — keeps the panel
  // light for buyers who aren't auditing). `historyExpanded` flips
  // the "Show all N" affordance on/off when the row count exceeds the
  // 10-row default.
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // The audit list reads against the current period only (the design
  // brief explicitly scopes audit by period — cross-period audit is a
  // future ticket). Period row comes from MS556's hook.
  const periodQuery = useCurrentBudgetPeriod(companyId);
  const currentPeriod = periodQuery.data ?? null;
  // Pull 50 rows by default — well above the "last 10 by default" UI
  // cap so "Show more" expands client-side without a refetch.
  const adjustmentsQuery = useAdjustmentsForPeriod(
    currentPeriod?.id ?? null,
    50,
  );

  // Payment method (saved card derived from the company query)
  const savedCard: SavedCard | null = (() => {
    const c = companyQuery.data;
    if (!c?.saved_card_last4) return null;
    return {
      last4: c.saved_card_last4,
      brand: c.saved_card_brand ?? "unknown",
      expMonth: c.saved_card_exp_month ?? 0,
      expYear: c.saved_card_exp_year ?? 0,
    };
  })();
  const [showCardForm, setShowCardForm] = useState(false);
  // MS245: separate state for the card-remove flow so the AlertDialog
  // owns its own open/close (we can't reuse showCardForm — that's the
  // Stripe Elements form).
  const [deletingCard, setDeletingCard] = useState(false);

  const handleDeleteCard = async () => {
    if (!companyId) return;
    setDeletingCard(true);
    try {
      const { error } = await supabase.functions.invoke("setup-payment-method", {
        body: { mode: "delete", companyId },
      });
      if (error) throw error;

      // MS479: auto-disable concierge autopilot when the card is removed.
      // Without this the DB flag stays `true`, the switch renders
      // checked+disabled, and the cron silently skips the company with
      // `skipped_no_card` — but the UI lies to the user about their state.
      // Non-fatal: a DB write failure here is logged but doesn't block the
      // card-removal UX (cron already guards via `skipped_no_card`).
      const wasAutopilotOn = conciergeAutopilot;
      if (wasAutopilotOn) {
        setConciergeAutopilot(false);
        const { error: autopilotErr } = await supabase
          .from("companies")
          .update({ concierge_autopilot_enabled: false })
          .eq("id", companyId);
        if (autopilotErr) {
          console.warn(
            "[Settings] autopilot auto-disable after card removal failed:",
            autopilotErr.message,
          );
        }
      }

      toast.success(
        wasAutopilotOn
          ? "Card removed. Concierge autopilot has been switched off. Add a card to re-enable."
          : "Card removed. Future orders will need a new card on file.",
      );
      invalidateCompany();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't remove card. Try again.");
    } finally {
      setDeletingCard(false);
    }
  };
  const [autoApproveBakery, setAutoApproveBakery] = useState<string>("");
  // Strategic autopilot flag (Sprint 1.3). When ON, qualified
  // employees (team_members.auto_eligible = true) auto-receive gifts
  // routed by the engine v2 per recipient. Distinct from the legacy
  // single-bakery `autopay_enabled` column (retired MS64e — the
  // scheduled-auto-approve-orders cron now reads only this flag).
  const [conciergeAutopilot, setConciergeAutopilot] = useState(false);

  // Notification prefs (MS388): live, backed by profiles.notification_prefs.
  // MS66 retired the original ghost-state version of this section because
  // there was no DB column for it. MS383 added the column, MS384–386
  // wired the read side into cron, and this restores the buyer-facing
  // write surface. State below mirrors the canonical jsonb shape parsed
  // by parseNotificationPrefs; the useEffect a few lines down hydrates
  // it whenever the profile query resolves/refreshes.
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>(
    DEFAULT_NOTIFICATION_PREFS,
  );
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);

  // Sync form drafts from the profile query whenever it resolves or
  // refreshes. Keeps the inputs in line with the server's truth.
  useEffect(() => {
    setFullNameDraft(profileFullName ?? "");
    setEmailDraft(profileEmail ?? "");
  }, [profileFullName, profileEmail]);

  // MS388: hydrate the live notification-prefs state from the profile
  // jsonb whenever the query resolves. Re-keying on the raw value (not
  // the parsed object) is intentional — parseNotificationPrefs is pure
  // so identical input → identical output; React reference equality on
  // the raw value is the cheap signal.
  const notifPrefsRaw = profileQuery.data?.notification_prefs;
  useEffect(() => {
    setNotifPrefs(parseNotificationPrefs(notifPrefsRaw));
  }, [notifPrefsRaw]);

  // Sync company-section drafts from the company query.
  useEffect(() => {
    const c = companyQuery.data;
    if (!c) return;
    companyForm.reset({
      name: c.name || "",
      industry: c.industry || "",
      teamSize: c.team_size || "",
    });
    // Sprint 2.5a /settings-fix, normalize legacy stored values to
    // the canonical bracket strings. A user who onboarded under a
    // pre-pivot version of the app could have a sub-canonical budget
    // bracket stored; normalizeMonthlyBudget maps it up to the
    // closest current equivalent so the Select shows the right value
    // selected (rather than the placeholder implying the preference
    // was lost).
    setMonthlyBudget(normalizeMonthlyBudget(c.monthly_budget));
    setAutoApproveBakery(c.auto_approve_bakery ?? "");
    setConciergeAutopilot(c.concierge_autopilot_enabled ?? false);
    // MS90: hydrate default_tier. Treat unknown stored values as ""
    // (placeholder) so the Select doesn't crash on a future enum
    // addition that pre-dates this page render.
    const storedTier = c.default_tier;
    setDefaultTier(
      storedTier === "standard" || storedTier === "premium" || storedTier === "luxury"
        ? storedTier
        : "",
    );
    // MS554: hydrate Budget panel state. Effective cadence is the one
    // currently driving period rollover; staged is the one queued for
    // the next boundary. We show the staged value when present so the
    // picker reflects the user's most recent intent. Null pool_cadence
    // (first-time companies) defaults to per_celebration per locked
    // decision; ceiling stays empty so we don't prefill a number.
    const effectiveCadence: PoolCadence =
      c.pool_cadence === "weekly" ||
      c.pool_cadence === "monthly" ||
      c.pool_cadence === "quarterly" ||
      c.pool_cadence === "per_celebration"
        ? c.pool_cadence
        : "per_celebration";
    const staged: PoolCadence | null =
      c.next_cadence === "weekly" ||
      c.next_cadence === "monthly" ||
      c.next_cadence === "quarterly" ||
      c.next_cadence === "per_celebration"
        ? c.next_cadence
        : null;
    setSavedEffectiveCadence(effectiveCadence);
    setStagedNextCadence(staged);
    setCadence(staged ?? effectiveCadence);
    // MS559: a null pool_cadence means this company has never saved the
    // Budget panel — show the get-started empty state. Any non-null
    // value (including per_celebration) counts as configured. Reset the
    // reveal flag on hydrate so re-loading the page for an unconfigured
    // company lands back on the empty state rather than a stale form.
    const configured = c.pool_cadence != null;
    setHasConfiguredBudget(configured);
    setBudgetSetupRevealed(false);
    // Pool size only makes sense in pooled mode. monthly_budget is the
    // legacy bracket field, not a dollar amount, so we don't seed
    // poolSizeInput from it. Once MS557 adds the period-level
    // allocated_cents read, we can hydrate from there. For now blank
    // means "user hasn't typed a pool size in this panel yet" — the
    // first save kicks off period creation downstream.
    setPoolSizeInput("");
    // Per-celebration ceiling: dollars from cents. null/0 → blank so
    // we never prefill a misleading number for unset companies.
    setPerCelebrationCeilingInput(
      typeof c.per_celebration_ceiling_cents === "number" &&
        c.per_celebration_ceiling_cents > 0
        ? String(c.per_celebration_ceiling_cents / 100)
        : "",
    );
    // Rollover default is ON per wireframes §2 — the "carry-over for
    // one period, then forfeit" framing is the recommended position.
    setRolloverOn(c.rollover_policy === "reset" ? false : true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyQuery.data]);

  // Keep the edit-address form in sync with the row currently being edited.
  useEffect(() => {
    if (!editingAddressId) return;
    const target = addresses.find((a) => a.id === editingAddressId);
    if (target) editAddressForm.reset({ label: target.label, address: target.address });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingAddressId]);

  const invalidateCompany = () => {
    if (!companyId) return;
    queryClient.invalidateQueries({ queryKey: ["companies", companyId] });
  };
  const invalidateAddresses = () => {
    if (!companyId) return;
    queryClient.invalidateQueries({ queryKey: ["company_addresses", companyId] });
  };
  const invalidateProfile = () => {
    if (!user?.id) return;
    queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
  };

  const saveName = async () => {
    if (!user) return;
    const trimmed = fullNameDraft.trim();
    if (!trimmed) {
      toast.error("Name can't be empty");
      return;
    }
    if (trimmed === (profileFullName ?? "")) return;
    setSavingName(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: trimmed })
      .eq("id", user.id);
    setSavingName(false);
    if (error) {
      toast.error("Couldn't update your name. Please try again.");
      return;
    }
    invalidateProfile();
    toast.success("Name updated");
  };

  // MS256: opening saveEmail now just opens the re-auth dialog; the
  // actual updateUser({email}) call lives in confirmEmailChange after
  // current-password verification succeeds.
  const saveEmail = () => {
    if (!user) return;
    const trimmed = emailDraft.trim();
    if (!trimmed) {
      toast.error("Email can't be empty");
      return;
    }
    if (trimmed === (profileEmail ?? "")) return;
    // MS401: validate format before opening the re-auth dialog so the
    // user gets immediate feedback instead of a confusing Supabase error
    // after typing their password.
    const emailCheck = emailSchema.safeParse(trimmed);
    if (!emailCheck.success) {
      toast.error(emailCheck.error.issues[0]?.message ?? "That doesn't look like a valid email address.");
      return;
    }
    setEmailReAuthPassword("");
    setEmailReAuthError("");
    setEmailReAuthOpen(true);
  };

  const confirmEmailChange = async () => {
    if (!user || !profileEmail) return;
    const trimmed = emailDraft.trim();
    if (!trimmed) return;
    if (!emailReAuthPassword) {
      setEmailReAuthError("Enter your current password.");
      return;
    }
    setSavingEmail(true);
    setEmailReAuthError("");

    // Probe the current password via signInWithPassword. On success
    // Supabase refreshes the session for the SAME user (no disruption
    // — the user stays logged in with a new token). On failure the
    // existing session is untouched.
    const { error: probeError } = await supabase.auth.signInWithPassword({
      email: profileEmail,
      password: emailReAuthPassword,
    });
    if (probeError) {
      setSavingEmail(false);
      setEmailReAuthError("That password didn't match. Try again.");
      return;
    }

    const { error } = await supabase.auth.updateUser({ email: trimmed });
    setSavingEmail(false);
    if (error) {
      setEmailReAuthError(error.message || "Couldn't update your email. Please try again.");
      return;
    }

    setEmailReAuthOpen(false);
    setEmailReAuthPassword("");
    toast.success(
      `Confirmation link sent to ${trimmed}. Click the link in that email to finish the change.`,
    );
  };

  const sendPasswordReset = async () => {
    if (!profileEmail) {
      toast.error("No email on file to send a reset link to.");
      return;
    }
    setSendingReset(true);
    const { error } = await resetPassword(profileEmail);
    setSendingReset(false);
    if (error) {
      toast.error("Couldn't send the reset link. Please try again.");
      return;
    }
    toast.success(`Reset link sent to ${profileEmail}. Click it to set a new password.`);
  };

  // MS64e: toggleAutopay removed. The autopay_enabled column it
  // toggled was retired in Sprint 1.3 when concierge_autopilot_enabled
  // took over as the cron's driver. The bakery dropdown still writes
  // auto_approve_bakery via updateAutoApproveBakery below — that
  // column is still actively read by the cron's BAKERIES whitelist.

  const updateAutoApproveBakery = useCallback(
    async (bakery: string) => {
      if (!companyId) return;
      setAutoApproveBakery(bakery);
      const { error } = await supabase
        .from("companies")
        .update({ auto_approve_bakery: bakery || null })
        .eq("id", companyId);
      if (error) {
        toast.error(error.message);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["companies", companyId] });
    },
    [companyId, queryClient],
  );

  // Sprint 1.3, concierge autopilot toggle. No bakery picker (engine
  // v2 routes per recipient using team_members.auto_eligible + the
  // recipient's profile signals). Optimistic update + rollback on error.
  const toggleConciergeAutopilot = useCallback(
    async (val: boolean) => {
      if (!companyId) return;
      if (val && !manualApprovalUnlocked) {
        toast.error(
          `Approve ${manualApprovalRemaining} more celebration${manualApprovalRemaining === 1 ? "" : "s"} manually on the dashboard to unlock autopilot.`,
        );
        return;
      }
      setConciergeAutopilot(val);
      const { error } = await supabase
        .from("companies")
        .update({ concierge_autopilot_enabled: val })
        .eq("id", companyId);
      if (error) {
        setConciergeAutopilot(!val);
        toast.error(error.message);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["companies", companyId] });
      // MS212: autopilot is cake-only today (scheduled-auto-approve-orders
      // cron). Honest toast copy so buyers don't assume every gift type
      // will auto-fire.
      toast.success(
        val
          ? "Concierge autopilot is on. We'll auto-fire cake orders for autopilot-eligible team members; other categories still need your approval."
          : "Concierge autopilot is off. We won't fire any gifts without your approval.",
      );
    },
    [companyId, queryClient, manualApprovalUnlocked, manualApprovalRemaining],
  );

  const onSaveCompanyInfo = async (values: CompanyInfoForm) => {
    if (!companyId) return;
    // MS94: persist team_size alongside name + industry. Empty → null
    // so the column stays nullable for legacy rows. Saved value feeds
    // the recommendation engine via MS79/80/MS94 → matters because
    // pricing buckets scale by team headcount.
    const { error } = await supabase
      .from("companies")
      .update({
        name: values.name.trim(),
        industry: values.industry || null,
        team_size: values.teamSize || null,
      })
      .eq("id", companyId);
    if (error) toast.error("Failed to save");
    else {
      invalidateCompany();
      toast.success("Company info updated");
    }
  };

  // MS388: persist notification_prefs as a single atomic jsonb write.
  // We send the WHOLE parsed object (rather than a JSON-merge-patch)
  // because:
  //   1. The state already mirrors the canonical default for any field
  //      the buyer hasn't touched, so we can't accidentally clobber a
  //      field that's not in the UI yet — there isn't one.
  //   2. RLS on profiles is auth.uid() = id, no extra policy needed.
  //   3. The chat agent's update_notification_pref / update_cadence
  //      tools do partial merges in their own paths; this UI is the
  //      "set everything I see here" path, which is cleaner UX for a
  //      multi-toggle Save action than per-control auto-save.
  const saveNotifPrefs = async () => {
    if (!user?.id) return;
    setSavingNotifPrefs(true);
    const { error } = await supabase
      .from("profiles")
      .update({ notification_prefs: notifPrefs as unknown as never })
      .eq("id", user.id);
    setSavingNotifPrefs(false);
    if (error) {
      toast.error("Couldn't save notification preferences. Try again.");
      return;
    }
    invalidateProfile();
    // The read-side enforcement (scheduled-celebration-notifications,
    // weekly-digest) runs once per day; "next deploy cycle" is the
    // honest phrasing — a buyer who flips weekly_digest off at 11pm
    // still gets that Monday morning's digest because cron has
    // already enqueued it.
    toast.success("Saved. New preferences take effect from tomorrow.");
  };

  const saveCelebrationPrefs = async () => {
    if (!companyId) return;
    // MS532: dropped celebration_type + celebration_style writes
    // because the two radios above them (AUDIT-013 + AUDIT-014) were
    // misleading users about what they controlled — neither value
    // was consulted by the recommendation engine or any downstream
    // consumer. The DB columns are intentionally retained so we can
    // repurpose the names for future company-level automation
    // defaults without a migration.
    const { error } = await supabase
      .from("companies")
      .update({
        monthly_budget: monthlyBudget || null,
        // MS90: persist default_tier alongside the other celebration
        // prefs (saved as part of the same "Save preferences" action
        // so the user mental model is "this is one settings group").
        // Empty string → null so we don't violate any future CHECK
        // constraint that requires standard/premium/luxury.
        default_tier: defaultTier || null,
      })
      .eq("id", companyId);
    if (error) toast.error("Failed to save");
    else {
      invalidateCompany();
      toast.success("Celebration preferences updated");
    }
  };

  // MS554: Budget panel save. Builds the patch from the four budget
  // columns (pool_cadence, per_celebration_ceiling_cents,
  // rollover_policy, next_cadence) according to the cadence-transition
  // rules in the dispatch brief + MS551 semantics.
  //
  //   - per_celebration ↔ pooled: write to pool_cadence directly,
  //     clear any staged next_cadence (the boundary doesn't apply
  //     across the no-pool gap).
  //   - pooled → pooled: write to next_cadence (only — pool_cadence
  //     stays put so the current period keeps its rhythm). Surface an
  //     inline "applies from next period" confirmation via toast +
  //     stagedNextCadence state.
  //   - No cadence change: write nothing for pool_cadence/next_cadence,
  //     just update ceiling + rollover.
  //
  // Pool size in this commit doesn't have a column on `companies` (it
  // lives on company_budget_periods.allocated_cents per MS551). MS557
  // wires that read/write; for now we surface the input so the UX is
  // complete + drop a non-fatal note for non-pooled saves. The value
  // is captured in component state and round-trips through controlled
  // input semantics so the regression test can assert "user-entered
  // pool size is preserved through the save click."
  const saveBudget = async () => {
    if (!companyId) return;
    setSavingBudget(true);
    const next = cadence;
    const prev = savedEffectiveCadence;
    const isPooledNext = next !== "per_celebration";
    const isPooledPrev = prev !== "per_celebration";
    const isPooledToPooled = isPooledNext && isPooledPrev && next !== prev;

    // Build patch. Type as Record<string, unknown> because the
    // generated supabase types may not yet include the four new
    // columns (regenerated post-MS554 merge); cast at the supabase
    // call site, not here, so the patch shape stays self-documenting.
    const patch: Record<string, unknown> = {};

    if (isPooledToPooled) {
      // Stage the change for the next period; leave pool_cadence put.
      patch.next_cadence = next;
    } else if (next !== prev) {
      // Crosses the per_celebration ↔ pooled boundary. Write the new
      // value to pool_cadence and clear any leftover staged value.
      patch.pool_cadence = next;
      patch.next_cadence = null;
    }
    // else: cadence unchanged, no pool_cadence/next_cadence write.

    // Per-celebration ceiling — always editable regardless of cadence
    // (it's the control-layer default per MS551 §4). Blank input → null
    // so unset stays unset; numeric → cents.
    const ceilingTrim = perCelebrationCeilingInput.trim();
    if (ceilingTrim === "") {
      patch.per_celebration_ceiling_cents = null;
    } else {
      const dollars = Number(ceilingTrim);
      if (!Number.isFinite(dollars) || dollars < 0) {
        setSavingBudget(false);
        toast.error("Per-celebration ceiling must be a positive number");
        return;
      }
      patch.per_celebration_ceiling_cents = Math.round(dollars * 100);
    }

    // Rollover policy is only meaningful when the active (or staged)
    // cadence is pooled. When the user is in per_celebration mode we
    // still write the toggle's current value so future cadence
    // changes inherit the user's preference rather than snapping back
    // to default — cheap, harmless, and avoids a UX surprise where
    // flipping back to monthly silently resets rollover.
    patch.rollover_policy = rolloverOn ? "carry_over" : "reset";

    const { error } = await supabase
      .from("companies")
      .update(patch as never)
      .eq("id", companyId);

    setSavingBudget(false);
    if (error) {
      toast.error(error.message || "Couldn't save your budget settings.");
      return;
    }
    invalidateCompany();

    // Reflect the staged change in local state so the inline
    // confirmation appears immediately, without waiting for the
    // companyQuery refetch to round-trip.
    if (isPooledToPooled) {
      setStagedNextCadence(next);
      toast.success(
        `Saved. New cadence applies from next period; the current period continues on ${prev}.`,
      );
    } else if (next !== prev) {
      setSavedEffectiveCadence(next);
      setStagedNextCadence(null);
      toast.success("Budget settings updated");
    } else {
      toast.success("Budget settings updated");
    }
  };

  const onAddAddress = async (values: AddressForm) => {
    if (!companyId) return;
    // MS388: title-case the address string on save (street + suburb).
    // Label stays user-defined verbatim — it's a human-chosen tag
    // ("HQ", "Branch", "Sydney") not an address-y string.
    const normalised = normalizeAddressString(values.address);
    const { error } = await supabase
      .from("company_addresses")
      .insert({
        company_id: companyId,
        label: values.label,
        address: normalised,
      });
    if (error) {
      toast.error("Failed to add address");
    } else {
      addAddressForm.reset({ label: "", address: "" });
      setShowAddAddress(false);
      invalidateAddresses();
      toast.success("Address added");
    }
  };

  const deleteAddress = async (id: string) => {
    // MS400: guard against double-tap — bail if any delete is already in flight
    if (deletingAddressId) return;
    setDeletingAddressId(id);
    try {
      const { error } = await supabase
        .from("company_addresses")
        .delete()
        .eq("id", id);
      if (error) toast.error("Failed to delete");
      else {
        invalidateAddresses();
        toast.success("Address removed");
      }
    } finally {
      setDeletingAddressId(null);
    }
  };

  const onSaveEditAddress = async (values: AddressForm) => {
    if (!editingAddressId) return;
    // MS388: same on-save title-casing as the add path. Editing an
    // existing address that came in lowercase is the second way bad
    // casing gets fixed (the first is re-saving on the add path).
    const normalised = normalizeAddressString(values.address);
    const { error } = await supabase
      .from("company_addresses")
      .update({ label: values.label, address: normalised })
      .eq("id", editingAddressId);
    if (error) toast.error("Failed to update");
    else {
      setEditingAddressId(null);
      invalidateAddresses();
      toast.success("Address updated");
    }
  };

  const handleDeleteAccount = async () => {
    // MS275: copy now matches behaviour — the dialog states explicitly
    // that this opens email to support, not a self-service delete. The
    // proper edge function is still a follow-up (tracked separately);
    // this PR just stopped the UI from lying about what the click does.
    const subject = encodeURIComponent("Delete my account");
    const body = encodeURIComponent(
      "Hi Lembra,\n\nPlease delete my account and all associated data.\n\nAccount email: ",
    );
    window.location.href = `mailto:support@lembra.com.au?subject=${subject}&body=${body}`;
  };

  if (!activeSection) return null;

  return (
    <>
      <div className="space-y-6 bg-cream rounded-xl p-6">
        {/* Account first — profile + sign-in safety before org config. */}
        {activeSection === "account" && (
        <div id="account" className="space-y-6">
        <SectionCard title="Account">
          <div className="space-y-5">
            <div>
              <Label className="text-navy font-medium">Full name</Label>
              <Input
                value={fullNameDraft}
                onChange={(e) => setFullNameDraft(e.target.value)}
                placeholder="Your name"
                className="mt-1"
              />
              <div className="mt-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveName}
                  disabled={
                    savingName || fullNameDraft.trim() === (profileFullName ?? "")
                  }
                >
                  {savingName && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {savingName ? "Saving…" : "Save name"}
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-navy font-medium">Email</Label>
              <Input
                type="email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                placeholder="you@company.com"
                className="mt-1"
              />
              <p className="mt-1 text-xs text-charcoal/60">
                Changing your email sends a confirmation link to the new address. The
                change only takes effect once you click that link.
              </p>
              <div className="mt-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveEmail}
                  disabled={
                    savingEmail || emailDraft.trim() === (profileEmail ?? "")
                  }
                >
                  {savingEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {savingEmail ? "Sending…" : "Save email"}
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-navy font-medium">Password</Label>
              <p className="mt-1 text-xs text-charcoal/60">
                We'll send a reset link to {profileEmail || "your email"}. Click the
                link to set a new password.
              </p>
              <div className="mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendPasswordReset}
                  disabled={sendingReset || !profileEmail}
                >
                  {sendingReset && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {sendingReset ? "Sending…" : "Change password"}
                </Button>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Delete your account" emphasis="Delete" danger>
          <p className="mb-4 text-sm text-charcoal/70">
            Account deletion is handled by our support team so we can verify
            it's you and complete any outstanding orders cleanly. We'll
            respond within one business day.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="primary" size="sm">
                Request deletion
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Request account deletion?</AlertDialogTitle>
                <AlertDialogDescription>
                  This opens your email app with a pre-filled message to
                  support@lembra.com.au. We'll confirm receipt and complete
                  the deletion within one business day. Your data stays
                  active until we confirm.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-coral text-white hover:bg-coral/90"
                  onClick={handleDeleteAccount}
                >
                  Open email
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </SectionCard>
        </div>
        )}

        {activeSection === "company" && (
        <div id="company" className="space-y-6">
        {/* 1. Company Information */}
        <SectionCard
          title="Company information"
          emphasis="information"
        >
          <Form {...companyForm}>
            <form onSubmit={companyForm.handleSubmit(onSaveCompanyInfo)} className="space-y-4">
              <FormField
                control={companyForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-navy font-medium">Company name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Acme Corp" className="mt-1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={companyForm.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-navy font-medium">Industry</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Sprint 2.5a /settings-fix: source from the
                            canonical INDUSTRY_OPTIONS so /settings and
                            /onboarding share one value space. Onboarding
                            writes the option verbatim (TitleCase), so we
                            store it the same way here — the prior
                            `value={i.toLowerCase()}` meant a buyer who
                            picked "Creative" in onboarding saw the select
                            fall back to the placeholder. */}
                        {INDUSTRY_OPTIONS.map((i) => (
                          <SelectItem key={i} value={i}>
                            {i}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* MS94: team_size moved out of onboarding-only territory.
                  Customers can keep the bucket up to date as they grow,
                  which matters because the recommendation engine sizes
                  pricing buckets by team headcount (per MS79/80/MS94). */}
              <FormField
                control={companyForm.control}
                name="teamSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-navy font-medium">Team size</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="How many people on your team?" />
                      </SelectTrigger>
                      <SelectContent>
                        {TEAM_SIZE_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1.5 text-xs text-charcoal/55">
                      Used to size each celebration's spend (more people →
                      bigger spreads). Update this when your headcount changes.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" variant="primary" size="sm" disabled={companyForm.formState.isSubmitting}>
                Save changes
              </Button>
            </form>
          </Form>
        </SectionCard>

        {/* 2. Office Addresses */}
        <SectionCard
          title="Office addresses"
          emphasis="addresses"
          description="Where we send the gift. Add as many offices as you have, and we'll route deliveries to the right one."
        >
          <div className="space-y-3">
            {addresses.length === 0 && !showAddAddress && (
              <p className="text-sm text-charcoal/50">
                We don't have a delivery address yet. Add your first office.
              </p>
            )}
            {addresses.map((a) =>
              editingAddressId === a.id ? (
                <Form key={a.id} {...editAddressForm}>
                  <form
                    onSubmit={editAddressForm.handleSubmit(onSaveEditAddress)}
                    className="space-y-2 rounded-lg border border-charcoal/10 p-3"
                  >
                    <FormField
                      control={editAddressForm.control}
                      name="label"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="Label" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editAddressForm.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            {/* MS289: Google Places autocomplete on the
                                edit-address row. onChange keeps RHF in
                                sync with manual typing; onPlaceSelect
                                fires the field's onChange with the
                                formatted address so the form picks it up. */}
                            <AddressAutocomplete
                              placeholder="Address"
                              {...field}
                              onPlaceSelect={(parsed) => field.onChange(parsed.formatted)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex gap-2">
                      <Button type="submit" variant="primary" size="sm" disabled={editAddressForm.formState.isSubmitting}>
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingAddressId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Form>
              ) : (
                <div
                  key={a.id}
                  className="flex items-center justify-between rounded-lg border border-charcoal/10 px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-medium text-navy">{a.label}</p>
                    <p className="text-xs text-charcoal/60">{a.address}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Edit address"
                      disabled={!!deletingAddressId}
                      onClick={() => setEditingAddressId(a.id)}
                    >
                      <Pencil className="h-4 w-4 text-charcoal/50" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete address"
                      disabled={deletingAddressId === a.id}
                      onClick={() => deleteAddress(a.id)}
                    >
                      <Trash2 className="h-4 w-4 text-coral" />
                    </Button>
                  </div>
                </div>
              )
            )}
            {showAddAddress ? (
              <Form {...addAddressForm}>
                <form
                  onSubmit={addAddressForm.handleSubmit(onAddAddress)}
                  className="space-y-2 rounded-lg border border-charcoal/10 p-3"
                >
                  <FormField
                    control={addAddressForm.control}
                    name="label"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input placeholder="e.g. Melbourne HQ" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={addAddressForm.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          {/* MS289: same Google Places autocomplete
                              shape as the edit row above. */}
                          <AddressAutocomplete
                            placeholder="e.g. 100 Collins St, Melbourne VIC 3000"
                            {...field}
                            onPlaceSelect={(parsed) => field.onChange(parsed.formatted)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex gap-2">
                    <Button type="submit" variant="primary" size="sm" disabled={addAddressForm.formState.isSubmitting}>
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowAddAddress(false);
                        addAddressForm.reset({ label: "", address: "" });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </Form>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddAddress(true)}
              >
                <Plus className="mr-1 h-4 w-4" /> Add address
              </Button>
            )}
          </div>
        </SectionCard>
        </div>
        )}

        {activeSection === "program" && (
        <>
        <SectionCard
          title="Program & budget"
          emphasis="budget"
          description="Defaults, spend pacing, and per-person gift ceilings for how Lembra recommends and spends on celebrations."
        />

        <div id="celebrations" className="space-y-6 scroll-mt-24">
        {/* 3. Celebration Preferences */}
        <SectionCard
          title="Celebration preferences"
          emphasis="preferences"
          description="The defaults Lembra uses when no one's said otherwise. You can override any of this per celebration."
        >
          <div className="space-y-5">
            {/* MS532: removed the "What do you celebrate?" (3-option)
                and "Celebration style" (4-option) radios. AUDIT-013
                + AUDIT-014: neither value was consulted by the
                recommendation engine, and both presented a misleading
                fraction of the real occasion / vendor catalogue.
                Per-event occasion + tier are the actual source of
                truth, collected per celebration on the order flow. */}
            <div>
              <Label className="text-navy font-medium">Monthly budget</Label>
              {/* Sprint 2.5a /settings-fix, sourced from the canonical
                  BUDGET_OPTIONS (MONTHLY PROGRAM BUDGET brackets). The
                  previous hardcoded list was sub-cake-cost per-event
                  ranges ("$50–$100" etc.), meaningless as a planning
                  input per the comment in companyPrefs.ts. Users with
                  legacy stored values get mapped to the closest
                  canonical bracket via normalizeMonthlyBudget on load. */}
              <Select value={monthlyBudget} onValueChange={setMonthlyBudget}>
                <SelectTrigger className="mt-1 w-64" aria-label="Monthly program budget">
                  <SelectValue placeholder="Select monthly budget" />
                </SelectTrigger>
                <SelectContent>
                  {BUDGET_OPTIONS.map((b) => (
                    <SelectItem key={b} value={b}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-charcoal/55">
                Your rough monthly total across all celebrations. We use it to
                suggest a sensible amount to spend on each gift.
              </p>
              <ExampleDisclosure>
                Example: set <strong>$1,000 a month</strong> across about 10
                celebrations and we&rsquo;ll suggest roughly{" "}
                <strong>$100 a gift</strong>. It guides our suggestions, not a
                hard limit.
              </ExampleDisclosure>
            </div>

            {/* MS90: company-level default tier. Wired into the
                recommendation engine in MS79 — the Dashboard's
                up-next list, per-celebration recommendations, and
                Approval modal all honour this when set (per-recipient
                tier_override on team_members still wins when present).
                Leaving it unset falls back to Thoughtful — the safe
                default for first-time use. */}
            <div>
              <Label className="text-navy font-medium">Default tier</Label>
              <Select
                value={defaultTier}
                onValueChange={(v) =>
                  setDefaultTier(v as "" | "standard" | "premium" | "luxury")
                }
              >
                <SelectTrigger className="mt-1 w-64" aria-label="Default tier">
                  <SelectValue placeholder="Thoughtful (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Thoughtful</SelectItem>
                  <SelectItem value="premium">Impressive</SelectItem>
                  <SelectItem value="luxury">Unforgettable</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-charcoal/55">
                The gift level we start from when you haven&rsquo;t set one for
                a specific person. You can still change it for anyone from
                their profile.
              </p>
              <ExampleDisclosure>
                Example: choose <strong>Impressive</strong> and we&rsquo;ll
                start a notch above <strong>Thoughtful</strong> for everyone,
                then bump a few people to <strong>Unforgettable</strong> on
                their profiles.
              </ExampleDisclosure>
            </div>

            <Button variant="primary" size="sm" onClick={saveCelebrationPrefs}>
              Save preferences
            </Button>
          </div>
        </SectionCard>

        {companyId && (
          <BulkCelebrationSettingsPanel
            companyId={companyId}
            addressOptions={(addressesQuery.data ?? []).map((a: CompanyAddressRow) => ({
              id: a.id,
              label: a.label || a.address || "Address",
            }))}
          />
        )}
        </div>

        <div id="tiers" className="space-y-6 scroll-mt-24">
        {/* 3b. Tiers — MS555 (Phase 2 budget feature).
              Custom tiers (Lucky's lock per MS552 §3 — named tiers were
              rejected in MS550 in favour of buyer-defined names). Lives
              between Celebration preferences and Notification cadence
              so it sits next to the "Default tier" select that's the
              fallback when no tier is assigned. Engine integration
              (tier ceilings overriding companies.per_celebration_ceiling_cents
              for assigned employees) is a separate future ticket; this
              PR ships CRUD + display only. */}
        <SectionCard
          title="Recognition tiers"
          emphasis="tiers"
          description="Give different employees different gift ceilings. Useful when senior team members or founding employees deserve a bigger budget than your default."
          id="tiers"
        >
          <TiersPanel companyId={companyId} />
        </SectionCard>
        </div>

        <div id="budget" className="space-y-6 scroll-mt-24">
        {/* 3c. Budget panel — MS554.
              The four budget columns (pool_cadence,
              per_celebration_ceiling_cents, rollover_policy,
              next_cadence) all live here. Per the locked decisions:
                - Settings is the only place these can be edited
                  (onboarding stays silent per MS532).
                - Cadence is the master switch — per_celebration hides
                  pool size + rollover. Pooled cadences show all
                  controls.
                - Cadence changes between two pooled values write to
                  next_cadence (current period continues; the rollover
                  cron applies the staged change at the next boundary).
                - per_celebration ↔ pooled writes pool_cadence directly.
              Tiers, dashboard widget, adjustments, and burn warnings
              are out of scope here (MS555–MS559). */}
        <SectionCard
          title="Budget"
          emphasis="Budget"
          description="How Lembra paces your spending. Per-celebration caps gifts individually; a pool cadence (weekly / monthly / quarterly) gives you a shared bucket that fills up at each period boundary."
          id="budget"
        >
          <div className="space-y-5" data-testid="budget-panel">
            {/* MS559: first-run get-started state. Shown when the
                company has never saved a cadence (null pool_cadence in
                the DB) and hasn't yet clicked "Set up my budget". Per
                wireframes §8 the buyer can read what a budget buys them,
                then opt in — or ignore it entirely and keep using the
                bracket fallback. Setting up a budget is optional; this
                panel never blocks the rest of the platform. */}
            {!hasConfiguredBudget && !budgetSetupRevealed ? (
              <div
                className="rounded-lg border border-dashed border-charcoal/20 p-6"
                data-testid="budget-empty-state"
              >
                <p className="text-sm font-medium text-navy">
                  You haven&rsquo;t set a budget yet.
                </p>
                <p className="mt-1.5 text-sm text-charcoal/70 leading-relaxed">
                  Right now, each celebration uses a default ceiling
                  based on your monthly budget bracket. Setting up a
                  budget gives you:
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-charcoal/70">
                  <li className="flex gap-2">
                    <span className="text-coral" aria-hidden>
                      &middot;
                    </span>
                    A shared pool that resets on a cadence you choose.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-coral" aria-hidden>
                      &middot;
                    </span>
                    Burn-rate forecasts before you overspend.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-coral" aria-hidden>
                      &middot;
                    </span>
                    Custom tiers for different team members.
                  </li>
                </ul>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    // Sensible first-run defaults per wireframes §8:
                    // monthly cadence + rollover on. Pool size + ceiling
                    // stay blank so we never prefill a misleading number.
                    setCadence("monthly");
                    setRolloverOn(true);
                    setBudgetSetupRevealed(true);
                  }}
                  data-testid="budget-empty-state-cta"
                >
                  Set up my budget
                </Button>
              </div>
            ) : (
              <>
            {/* "How does this work?" explainer — click-to-expand
                inline (not a modal — modals are reserved for
                destructive flows per wireframes §2). */}
            <Collapsible
              open={budgetExplainerOpen}
              onOpenChange={setBudgetExplainerOpen}
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-coral hover:text-coral/80"
                  data-testid="budget-explainer-toggle"
                >
                  How does this work?
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform ${
                      budgetExplainerOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent
                className="mt-2 rounded-lg border border-charcoal/10 bg-white p-4 text-sm text-charcoal/75 leading-relaxed"
                data-testid="budget-explainer-content"
              >
                {cadence === "per_celebration" ? (
                  <p>
                    Each celebration uses your default per-celebration ceiling
                    unless the recipient has a tier ceiling. There&rsquo;s no
                    shared pool. Lembra never proposes a gift over this number.
                  </p>
                ) : (
                  <p>
                    Your pool refills at the start of each {cadence === "weekly" ? "week" : cadence === "monthly" ? "month" : "quarter"}.
                    Spend during the period draws from the pool; the dashboard
                    shows what&rsquo;s left. If carry-over is on, any unspent
                    balance rolls into the next period (then forfeits if still
                    unused). The per-celebration ceiling caps each individual
                    gift, even when the pool has plenty left.
                  </p>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Cadence picker — segmented radio, single source of
                truth per wireframes §2. RadioGroup is what shadcn
                exposes; we lay it out horizontally + style each item
                as a chip for the segmented control look. */}
            <div>
              <Label className="text-navy font-medium">Cadence</Label>
              <p className="mt-0.5 text-xs text-charcoal/55">
                How Lembra paces spending across your team.
              </p>
              <RadioGroup
                value={cadence}
                onValueChange={(v) => setCadence(v as PoolCadence)}
                className="mt-2 flex flex-wrap gap-2"
                aria-label="Budget cadence"
              >
                {[
                  { value: "per_celebration" as const, label: "Per celebration" },
                  { value: "weekly" as const, label: "Weekly" },
                  { value: "monthly" as const, label: "Monthly" },
                  { value: "quarterly" as const, label: "Quarterly" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      cadence === opt.value
                        ? "border-coral bg-coral/5 text-navy font-semibold"
                        : "border-charcoal/15 text-charcoal/75 hover:border-charcoal/30"
                    }`}
                  >
                    <RadioGroupItem
                      value={opt.value}
                      data-testid={`budget-cadence-${opt.value}`}
                    />
                    {opt.label}
                  </label>
                ))}
              </RadioGroup>
              {/* Inline confirmation when the user has staged a
                  pooled→pooled change. Pulls double duty: surfaces the
                  staged value after save, and renders before save when
                  the picker selection differs from the effective
                  cadence but matches "what's already staged" (e.g.
                  the user reopened the page and the staged value is
                  still queued). */}
              {stagedNextCadence &&
                stagedNextCadence !== savedEffectiveCadence && (
                  <p
                    className="mt-2 text-xs text-charcoal/65"
                    data-testid="budget-cadence-staged-note"
                  >
                    This change applies from next period; current period
                    continues on{" "}
                    <span className="font-semibold text-navy">
                      {savedEffectiveCadence === "per_celebration"
                        ? "per-celebration"
                        : savedEffectiveCadence}
                    </span>
                    .
                  </p>
                )}
            </div>

            {/* Per-celebration cadence: hide pool + rollover, show a
                short note. Per-celebration ceiling stays visible
                because it's always the company-wide control-layer
                default per MS551 §4. */}
            {cadence === "per_celebration" ? (
              <div
                className="rounded-lg border border-charcoal/10 bg-butter/20 px-3 py-2 text-xs text-charcoal/70"
                data-testid="budget-per-celebration-note"
              >
                No pool to track; we cap individual gifts at the
                per-celebration ceiling below.
              </div>
            ) : (
              <>
                {/* Pool size input — only shown for pooled cadences.
                    The numeric input round-trips through component
                    state; MS557 wires the actual companies/period
                    write. We surface it now so the UX is complete and
                    the regression test can assert the field renders
                    + accepts input. */}
                <div data-testid="budget-pool-size-row">
                  <Label
                    htmlFor="budget-pool-size"
                    className="text-navy font-medium"
                  >
                    Pool size per period
                    <span className="ml-2 rounded-full bg-charcoal/[0.06] px-2 py-0.5 align-middle text-[10px] font-medium uppercase tracking-wider text-charcoal/55">
                      Coming soon
                    </span>
                  </Label>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-charcoal/40">
                        $
                      </span>
                      {/* Disabled until MS557 wires the companies/period
                          write. The value isn't persisted yet, so leaving
                          it editable + showing a success toast on save was
                          a data-loss trap: the buyer set a pool, saw
                          "saved", and lost it on reload. */}
                      <Input
                        id="budget-pool-size"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="50"
                        placeholder="0.00"
                        value={poolSizeInput}
                        onChange={(e) => setPoolSizeInput(e.target.value)}
                        className="w-40 pl-7"
                        aria-label="Pool size per period (AUD)"
                        disabled
                      />
                    </div>
                    <span className="text-sm text-charcoal/60">
                      per{" "}
                      {cadence === "weekly"
                        ? "week"
                        : cadence === "monthly"
                          ? "month"
                          : "quarter"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-charcoal/55">
                    Shared pool tracking is coming soon, so this isn&rsquo;t
                    saved yet. For now, each gift is capped by the
                    per-celebration ceiling below.
                  </p>
                </div>

                {/* Rollover toggle — only shown for pooled cadences.
                    Default ON per wireframes §2 + locked decisions. */}
                <div
                  className="flex items-start justify-between gap-4"
                  data-testid="budget-rollover-row"
                >
                  <div className="flex-1">
                    <Label
                      htmlFor="budget-rollover"
                      className="text-sm font-semibold text-navy cursor-pointer"
                    >
                      Rollover unused budget
                    </Label>
                    <p className="mt-0.5 text-xs text-charcoal/65 max-w-md">
                      Carry-over for one period, then forfeit. Helps you
                      avoid end-of-period splurge on rushed gifts.
                    </p>
                  </div>
                  <Switch
                    id="budget-rollover"
                    checked={rolloverOn}
                    onCheckedChange={setRolloverOn}
                    aria-label="Toggle rollover policy"
                  />
                </div>

                {/* MS557: "Adjust this period" — the Settings entry
                    point into the mid-period adjustment modal. Only
                    rendered when there's an active period to adjust;
                    new companies between cadence-save and first period
                    creation see no button (the row would have no
                    period_id to write against). */}
                {currentPeriod ? (
                  <div
                    className="rounded-lg border border-charcoal/10 bg-cream/30 p-3"
                    data-testid="budget-adjustment-entry-row"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-navy">
                          Adjust this period
                        </div>
                        <p className="mt-0.5 text-xs text-charcoal/65 max-w-md">
                          Top up or reduce the current period&rsquo;s
                          pool without changing your recurring budget.
                          Every change is logged below.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAdjustOpen(true)}
                        data-testid="budget-adjustment-open"
                      >
                        Adjust this period
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            )}

            {/* Default per-celebration ceiling — always visible
                regardless of cadence (control-layer default per MS551
                §4). Caption names the fallback so the buyer
                understands the role of this number. */}
            <div data-testid="budget-ceiling-row">
              <Label
                htmlFor="budget-ceiling"
                className="text-navy font-medium"
              >
                Default per-celebration ceiling
              </Label>
              <div className="mt-1 relative w-40">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-charcoal/55">
                  $
                </span>
                <Input
                  id="budget-ceiling"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="10"
                  placeholder="0.00"
                  value={perCelebrationCeilingInput}
                  onChange={(e) =>
                    setPerCelebrationCeilingInput(e.target.value)
                  }
                  className="pl-7"
                  aria-label="Default per-celebration ceiling (AUD)"
                />
              </div>
              <p className="mt-1 text-xs text-charcoal/55">
                Applies when no tier ceiling is set on the recipient.
              </p>
            </div>

            <Button
              variant="primary"
              size="sm"
              onClick={saveBudget}
              disabled={savingBudget}
              data-testid="budget-save"
            >
              {savingBudget && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {savingBudget ? "Saving…" : "Save budget"}
            </Button>

            {/* MS557: audit history surface. Read-only list of every
                adjustment for the current period, most recent first.
                Collapsed by default — the panel stays light for the
                90% of buyers who never adjust. The list refetches via
                the mutation's invalidation, so a just-inserted row
                lands at the top within a tick of the toast firing.
                Per wireframes §5: each row shows date / signed amount
                / kind / reason / actor. Last-10 cap with "Show all"
                expansion when the count exceeds 10.
                Hidden entirely when no active period exists — the
                drawer would be misleading (it would show the wrong
                period's audit). */}
            {currentPeriod ? (
              <BudgetAdjustmentsHistory
                adjustments={adjustmentsQuery.data?.adjustments ?? []}
                isLoading={adjustmentsQuery.isLoading}
                isOpen={historyOpen}
                onToggle={() => setHistoryOpen((v) => !v)}
                expanded={historyExpanded}
                onToggleExpanded={() =>
                  setHistoryExpanded((v) => !v)
                }
              />
            ) : null}

            {/* Shared adjustment modal — same component the dashboard
                pool widget mounts. Hidden when no period is loaded. */}
            <BudgetAdjustmentModal
              open={adjustOpen}
              onOpenChange={setAdjustOpen}
              periodId={currentPeriod?.id ?? null}
              companyId={companyId}
              createdBy={profileQuery.data?.id ?? null}
              periodSubtitle={
                currentPeriod
                  ? `${formatPeriodLabel(currentPeriod.cadence_type, currentPeriod.period_start)}`
                  : undefined
              }
            />
              </>
            )}
          </div>
        </SectionCard>
        </div>
        </>
        )}

        {activeSection === "notifications" && (
        <div id="notifications" className="space-y-6">
        {/* 4. Notification cadence — MS388: live toggles wired to
              profiles.notification_prefs. MS66 retired the original
              ghost-state version because no DB column backed it. MS383
              added the column + the canonical shape; MS384–386 wired
              the read side into the scheduled jobs that send these
              emails. This section restores the buyer-facing write
              surface, sharing parseNotificationPrefs +
              DEFAULT_NOTIFICATION_PREFS with the edge-function read
              path so what's toggled here is exactly what cron honours.
              Honest "next deploy cycle" toast on save — daily cron has
              already enqueued tonight's mail when the buyer toggles.
              MS531 (2026-05-28) audit confirmed every key on the
              canonical shape is read AND honored:
                - emails.celebration_reminders → scheduled-celebration-
                  notifications gates the email send via shouldSendEmail
                  (line ~395). OFF = no email, in-platform notif still
                  fires.
                - emails.order_updates → scheduled-auto-approve-orders
                  gates both the auto-create email (line ~823) and the
                  per-order admin-update path (line ~1102). Also
                  honored by scheduled-monthly-summary (line ~214).
                - emails.weekly_digest → scheduled-weekly-digest filters
                  eligibleAdmins on prefs.emails.weekly_digest === true
                  (line ~187). OFF = admin never enters the loop.
                - cadence.celebration_lead_days → scheduled-celebration-
                  notifications uses it to pick the "lead" reminder
                  fire day (line ~210/222). Default 1 (morning before),
                  clamped 0–30 by parseNotificationPrefs.
                - cadence.digest_day_of_week → scheduled-weekly-digest
                  eligibility filter (line ~188). Lowercase day name,
                  fallback to "monday" if invalid.
              See docs/audits/notification-prefs-wiring.md for the full
              evidence table. Adding a new field here without wiring
              the read side is the failure mode this audit guards
              against — keep the canonical shape in
              _shared/notification-prefs.ts + src/lib/notificationPrefs.ts
              in lockstep, and update the settings-copy.test.ts guard. */}
        <SectionCard
          title="Notification frequency"
          emphasis="frequency"
          description="Turn off the touchpoints you don't want, and tell us when to remind you. New preferences take effect from tomorrow."
        >
          <div className="space-y-5">
            {/* Email toggles. Switch + Label is the same pattern used
                in Concierge Autopilot above and Account elsewhere on
                this page, so the visual rhythm stays consistent. */}
            <div className="space-y-4">
              <NotificationToggleRow
                id="notif-celebration-reminders"
                title="Celebration reminders"
                description="A heads-up before each team member's birthday or work anniversary."
                checked={notifPrefs.emails.celebration_reminders}
                onCheckedChange={(checked: boolean) =>
                  setNotifPrefs((prev) => ({
                    ...prev,
                    emails: { ...prev.emails, celebration_reminders: checked },
                  }))
                }
              />
              <NotificationToggleRow
                id="notif-order-updates"
                title="Order updates"
                description="Order confirmations, vendor accepted, out for delivery, arrived."
                checked={notifPrefs.emails.order_updates}
                onCheckedChange={(checked: boolean) =>
                  setNotifPrefs((prev) => ({
                    ...prev,
                    emails: { ...prev.emails, order_updates: checked },
                  }))
                }
              />
              <NotificationToggleRow
                id="notif-weekly-digest"
                title="Weekly digest"
                description="A Monday-morning summary of everything coming up that week."
                checked={notifPrefs.emails.weekly_digest}
                onCheckedChange={(checked: boolean) =>
                  setNotifPrefs((prev) => ({
                    ...prev,
                    emails: { ...prev.emails, weekly_digest: checked },
                  }))
                }
              />
            </div>

            {/* Cadence knobs — controlled by a select rather than a
                free number input so the buyer can't accidentally type
                "100" (the schema allows 0–30 but a 30-day reminder is
                operationally weird). The discrete options also match
                the values the chat agent suggests when buyers ask "how
                far in advance can you remind me?". */}
            <div className="grid gap-4 sm:grid-cols-2 pt-1">
              <div>
                <Label
                  htmlFor="notif-lead-days"
                  className="text-navy font-medium"
                >
                  How far ahead should we remind you?
                </Label>
                <Select
                  value={String(notifPrefs.cadence.celebration_lead_days)}
                  onValueChange={(v) =>
                    setNotifPrefs((prev) => ({
                      ...prev,
                      cadence: {
                        ...prev.cadence,
                        celebration_lead_days: Number.parseInt(v, 10),
                      },
                    }))
                  }
                >
                  <SelectTrigger
                    id="notif-lead-days"
                    className="mt-1"
                    aria-label="Celebration reminder lead time"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CELEBRATION_LEAD_DAY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label
                  htmlFor="notif-digest-day"
                  className="text-navy font-medium"
                >
                  Which day should the weekly digest land?
                </Label>
                <Select
                  value={notifPrefs.cadence.digest_day_of_week}
                  onValueChange={(v) =>
                    setNotifPrefs((prev) => ({
                      ...prev,
                      cadence: { ...prev.cadence, digest_day_of_week: v as DayOfWeek },
                    }))
                  }
                  disabled={!notifPrefs.emails.weekly_digest}
                >
                  <SelectTrigger
                    id="notif-digest-day"
                    className="mt-1"
                    aria-label="Weekly digest day of week"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_OF_WEEK_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!notifPrefs.emails.weekly_digest && (
                  <p className="mt-1.5 text-xs text-charcoal/55">
                    Turn on Weekly digest above to pick a day.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Button
                variant="primary"
                size="sm"
                onClick={saveNotifPrefs}
                disabled={savingNotifPrefs}
              >
                {savingNotifPrefs && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {savingNotifPrefs ? "Saving…" : "Save preferences"}
              </Button>
              <p className="text-xs text-charcoal/55">
                Critical alerts (payment failures, security) always send,
                regardless of these toggles.
              </p>
            </div>
          </div>
        </SectionCard>
        </div>
        )}

        {activeSection === "payment" && (
        <div id="payment" className="space-y-6">
        {/* 5. Payment Method */}
        <SectionCard
          title="Payment method"
          emphasis="method"
          id="payment"
        >
          <Elements stripe={stripePromise}>
            <div className="space-y-4">
              {savedCard ? (
                <div className="flex items-center gap-3 rounded-lg border border-charcoal/10 bg-white px-4 py-3">
                  <CreditCard className="h-5 w-5 text-navy shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-navy">
                      {brandLabel(savedCard.brand)} ending in {savedCard.last4}
                    </p>
                    <p className="text-xs text-charcoal/50">
                      Expires {String(savedCard.expMonth).padStart(2, "0")}/{savedCard.expYear}
                    </p>
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                </div>
              ) : (
                <p className="text-sm text-charcoal/50">No card saved yet.</p>
              )}

              {showCardForm ? (
                <CardSetupForm
                  companyId={companyId ?? ""}
                  userEmail={profileEmail ?? ""}
                  companyName={companyName}
                  onSaved={() => {
                    // savedCard is derived from the company query's
                    // saved_card_last4 column, so we just invalidate and
                    // let the refetch hydrate the card display.
                    invalidateCompany();
                    setShowCardForm(false);
                  }}
                />
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCardForm(true)}
                    disabled={!companyId || deletingCard}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {savedCard ? "Update card" : "Add card"}
                  </Button>
                  {/* MS245: only render Remove when a card is on file.
                      Confirm via AlertDialog because detaching is
                      destructive — handleDeleteCard also switches
                      Concierge autopilot off (sets
                      concierge_autopilot_enabled = false), so the dialog
                      copy below states that outcome plainly rather than
                      the old "orders will fail" framing. */}
                  {savedCard && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!companyId || deletingCard}
                          className="text-coral hover:text-coral hover:bg-coral/10"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {deletingCard ? "Removing…" : "Remove card"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove this card?</AlertDialogTitle>
                          <AlertDialogDescription>
                            We'll detach{" "}
                            <strong>
                              {brandLabel(savedCard.brand)} ending in {savedCard.last4}
                            </strong>{" "}
                            from your Lembra workspace and from Stripe. This
                            also switches Concierge autopilot off, since we
                            can&rsquo;t send gifts automatically without a card
                            on file.
                            Re-add a card here anytime to turn it back on.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep card</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDeleteCard}>
                            Remove card
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              )}

              {/* MS64e: the legacy "Auto-approve celebrations" toggle
                  (autopay_enabled column) was retired in Sprint 1.3
                  when concierge_autopilot_enabled took over. The
                  scheduled-auto-approve-orders cron now reads the new
                  flag exclusively; the old column still exists on the
                  row but nothing reads it. The single-bakery default
                  picker still drives the cron's vendor choice though,
                  so it's been relocated into the Concierge autopilot
                  section below where it operationally belongs. */}
            </div>
          </Elements>
        </SectionCard>
        </div>
        )}

        {activeSection === "autopilot" && (
        <div id="autopilot" className="space-y-6">
        {/* 5b. Concierge Autopilot, Sprint 1.3.
              The new strategic flag (concierge_autopilot_enabled). Distinct
              from the legacy "Auto-approve celebrations" toggle inside the
              Payment Method section, which still uses the single-bakery
              `autopay_enabled` flag. The legacy section stays for backward
              compatibility; this new section is what we surface as the
              primary autopilot UX going forward. */}
        <SectionCard
          title="Concierge autopilot"
          emphasis="autopilot"
          description="Autopilot handles cake orders for now. Lembra queues each one ahead of time, and you get a 24-hour window to cancel before we charge your saved card. Other gift types still need your approval each time."
          id="autopilot"
        >
          <div className="space-y-5">
            {!manualApprovalUnlocked && !conciergeAutopilot && (
              <div className="rounded-xl border-2 border-charcoal/10 bg-butter/20 p-4">
                <p className="text-sm font-semibold text-navy">
                  {manualApprovalCount} of {MANUAL_APPROVALS_FOR_AUTOPILOT} manual approvals
                </p>
                <p className="mt-1 text-xs text-charcoal/65 max-w-md">
                  Approve {MANUAL_APPROVALS_FOR_AUTOPILOT} celebrations yourself first. After that, autopilot unlocks and we can charge your saved card automatically.
                </p>
              </div>
            )}

            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-navy flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-coral" />
                  Auto-fire gifts for autopilot-eligible team members
                </p>
                <p className="text-xs text-charcoal/65 mt-1 max-w-md">
                  When ON, Lembra automatically sends a cake on each
                  autopilot-eligible team member's birthday and work
                  anniversary. Eligibility is per-person, it depends on
                  how much you've told us about each one (hobbies, food
                  preferences, dietary status). You stay in control via
                  the per-employee profile pages.
                </p>
                {/* MS388: card-required guard. Pre-MS388 the buyer
                    could flip autopilot ON with no card, then
                    scheduled-auto-approve-orders (~L965) would fire
                    the Stripe charge, fail, and silently send a
                    paymentFailed email. We now hard-disable the toggle
                    until a card exists. The toggle is intentionally
                    not auto-enabled when a card is added — the buyer
                    still consciously turns autopilot on after seeing
                    the eligibility numbers below. */}
                {!savedCard && (
                  <p className="mt-2 text-xs text-charcoal/65">
                    A saved card is required so we can charge for each
                    auto-approved order.{" "}
                    <Link
                      to="/settings/payment"
                      className="font-semibold text-coral hover:underline"
                    >
                      Save a card first →
                    </Link>
                  </p>
                )}
              </div>
              <Switch
                checked={conciergeAutopilot}
                onCheckedChange={toggleConciergeAutopilot}
                disabled={!savedCard || (!conciergeAutopilot && !manualApprovalUnlocked)}
                aria-label="Toggle concierge autopilot"
              />
            </div>

            {/* Live eligibility summary */}
            <div className="rounded-xl border-2 border-charcoal/10 bg-butter/20 p-4">
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <p className="text-sm font-semibold text-navy">
                  Team eligibility right now
                </p>
                {eligibilityQuery.isLoading ? (
                  <span className="text-xs text-charcoal/50">Loading…</span>
                ) : (
                  <p className="text-sm text-charcoal/75">
                    <span className="font-bold text-navy">
                      {eligibilityQuery.data?.eligible ?? 0}
                    </span>
                    {" of "}
                    <span className="font-semibold text-navy">
                      {eligibilityQuery.data?.total ?? 0}
                    </span>
                    {" eligible"}
                  </p>
                )}
              </div>
              {!eligibilityQuery.isLoading && (eligibilityQuery.data?.total ?? 0) > 0 && (
                <>
                  {/* Tiny progress bar */}
                  <div className="h-1.5 w-full rounded-full bg-charcoal/10 overflow-hidden mb-3">
                    <div
                      className="h-full bg-coral rounded-full transition-all"
                      style={{
                        width: `${Math.round(
                          ((eligibilityQuery.data?.eligible ?? 0) /
                            Math.max(1, eligibilityQuery.data?.total ?? 1)) *
                            100,
                        )}%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-charcoal/65">
                      {(eligibilityQuery.data?.eligible ?? 0) === (eligibilityQuery.data?.total ?? 0)
                        ? "Every active team member is autopilot-eligible. Nice."
                        : `Add hobbies, food preferences, or dietary status to the rest to grow this number.`}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/team")}
                      className="shrink-0"
                    >
                      Review the team
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </>
              )}
              {!eligibilityQuery.isLoading && (eligibilityQuery.data?.total ?? 0) === 0 && (
                <p className="text-xs text-charcoal/65">
                  Add team members first, then add their hobbies, food preferences, or dietary needs to make them autopilot-eligible.
                </p>
              )}
            </div>

            {/* MS64e: bakery default for autopilot orders. Relocated
                from the legacy Payment Method → Auto-approve sub-
                section, which has been retired. The
                scheduled-auto-approve-orders cron still requires a
                bakery to be set on the company row (BAKERIES whitelist
                in the function); the five options here mirror that
                whitelist. Greyed out when autopilot is off, since
                nothing fires without the toggle. The cron also
                requires a saved card (Payment Method above) — if
                that's missing we surface the same warning we used to. */}
            {/* MS392: collapsed `{savedCard ? (bakery picker) : (conciergeAutopilot
                && warning)}` to `{savedCard && (bakery picker)}`. The else
                branch was unreachable after MS388 — that PR made the
                autopilot Switch `disabled={!savedCard}`, so the only path
                that could surface `conciergeAutopilot && !savedCard` was
                a legacy DB state (autopilot was previously turned on,
                then the card was removed). The MS388 toggle-level guard
                + the card-required line above the Switch (line 1834) is
                now the canonical "you need a card" surface. Hiding the
                bakery picker entirely when there's no card is the cleanest
                downstream signal. */}
            {savedCard && (
              <div className={`rounded-xl border-2 border-charcoal/10 bg-white p-4 ${conciergeAutopilot ? "" : "opacity-60"}`}>
                {/* MS212: label was "Default vendor for autopilot orders"
                    but the cron (scheduled-auto-approve-orders) is
                    cake-only — the BAKERIES whitelist contains exactly
                    five bakeries and auto-fired orders go to one of
                    them. "Vendor" framing over-promised that autopilot
                    handled hampers / flowers / spa vouchers too, which
                    it doesn't. Honest label + clarifying helper text
                    so the buyer understands the scope. */}
                <Label className="text-xs font-semibold text-navy">
                  Default cake bakery for autopilot orders {conciergeAutopilot && <span className="text-coral">*</span>}
                </Label>
                <Select
                  value={autoApproveBakery}
                  onValueChange={updateAutoApproveBakery}
                  disabled={!companyId}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Pick the bakery Lembra should default to" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cupcake Central">Cupcake Central · Thoughtful tier</SelectItem>
                    <SelectItem value="Little Cupcakes">Little Cupcakes · Thoughtful tier</SelectItem>
                    <SelectItem value="Goldelucks">Goldelucks · Thoughtful tier</SelectItem>
                    <SelectItem value="Le Yeahllow">Le Yeahllow · Impressive tier</SelectItem>
                    <SelectItem value="Chéri">Chéri · Unforgettable tier</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-2 text-xs text-charcoal/60">
                  Autopilot currently only auto-fires cake orders. Package
                  size + price is chosen automatically based on your team
                  size and the bakery's tier. For hampers, flowers, spa
                  vouchers and other categories, gifts still need your
                  approval each time. (Per-recipient vendor routing across
                  all categories is coming with the next engine update.)
                </p>
              </div>
            )}

            {/* Sub-explainer when toggle is ON but no one is eligible, Norman:
                explicit feedback so the user understands "the toggle is on
                but nothing will fire yet." */}
            {conciergeAutopilot &&
              !eligibilityQuery.isLoading &&
              (eligibilityQuery.data?.eligible ?? 0) === 0 && (
                <div className="rounded-xl border border-coral/30 bg-coral/5 p-3">
                  <p className="text-xs text-coral">
                    Autopilot is on, but no team members are eligible yet, so
                    nothing will auto-fire. Open a team member's profile and
                    add a hobby or food preference to qualify them.
                  </p>
                </div>
              )}

            {/* Upcoming auto-fires preview, only renders when autopilot
                is actually ON and we have data, so users see what's
                queued in real terms. Per Nielsen #1 (visibility of system
                status), they should never wonder "what's about to happen?". */}
            {conciergeAutopilot && !upcomingFiresQuery.isLoading && (upcomingFiresQuery.data?.length ?? 0) > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-navy">
                  Coming up, auto-fires in the next 30 days
                </p>
                <ul className="rounded-xl border-2 border-charcoal/10 bg-white divide-y divide-charcoal/10">
                  {(upcomingFiresQuery.data ?? []).map((cel) => {
                    const tm = Array.isArray(cel.team_members) ? cel.team_members[0] : cel.team_members;
                    const date = new Date(cel.date + "T00:00:00");
                    const daysOut = Math.max(0, Math.round((date.getTime() - Date.now()) / 86400000));
                    // MS107: vocabulary unified with Calendar /
                    // useNotificationGenerator / Dashboard humanType /
                    // Billing celebrationLabel. Pre-MS107 this said
                    // "welcome" while Calendar said "First Day" for
                    // the same type — and "milestone" fell through
                    // the chain to raw type ("milestone") which
                    // happened to read OK but wasn't intentional.
                    const occasionLabel =
                      cel.type === "birthday" ? "birthday" :
                      cel.type === "work_anniversary" ? "work anniversary" :
                      cel.type === "welcome" ? "first day" :
                      cel.type === "farewell" ? "farewell" :
                      cel.type === "milestone" ? "milestone" :
                      "celebration";
                    return (
                      <li key={cel.id} className="flex items-center justify-between px-4 py-2.5">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-navy truncate">
                            {tm?.name ?? "Team member"}
                          </p>
                          <p className="text-xs text-charcoal/65">
                            {occasionLabel} · {daysOut === 0 ? "today" : daysOut === 1 ? "tomorrow" : `in ${daysOut} days`}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`/team/${tm?.id}/profile`)}
                          className="text-xs text-coral hover:text-coral/70 shrink-0"
                        >
                          Profile →
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-charcoal/55">
                  We queue each order 14 days before the celebration. You'll get
                  email + a 24-hour grace window to cancel before the card is charged.
                </p>
              </div>
            )}
          </div>
        </SectionCard>
        </div>
        )}
      </div>

      {/* MS256: email-change re-auth gate. Confirms the current password
          before firing the Supabase email-change flow so a session
          hijack (shared device, stolen unlocked laptop) can't pivot to
          email change → password reset → lockout. */}
      <AlertDialog
        open={emailReAuthOpen}
        onOpenChange={(open) => {
          if (!open && !savingEmail) {
            setEmailReAuthOpen(false);
            setEmailReAuthPassword("");
            setEmailReAuthError("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm your password to change email</AlertDialogTitle>
            <AlertDialogDescription>
              You're changing your account email to{" "}
              <strong>{emailDraft.trim()}</strong>. Re-enter your current
              password so we know it's you, not someone who borrowed your
              session.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Input
              type="password"
              autoComplete="current-password"
              value={emailReAuthPassword}
              onChange={(e) => {
                setEmailReAuthPassword(e.target.value);
                if (emailReAuthError) setEmailReAuthError("");
              }}
              placeholder="Current password"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !savingEmail) {
                  e.preventDefault();
                  void confirmEmailChange();
                }
              }}
            />
            {emailReAuthError && (
              <p className="text-sm text-coral">{emailReAuthError}</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingEmail}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmEmailChange();
              }}
              disabled={savingEmail || !emailReAuthPassword}
            >
              {savingEmail ? "Verifying…" : "Confirm + send link"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
